/**
 * Shape of a legal document page (Privacy Policy, Terms & Conditions, …).
 *
 * The copy for these pages is legal text, so it is never authored in a component
 * — each page keeps a content module that reproduces its source verbatim and
 * hands it to <LegalDoc />.
 *
 * ── EVERY TEXT FIELD BELOW IS A TRANSLATION KEY ─────────────────────────────
 * Not English. `LegalDoc` runs each one through `t()`.
 *
 * The keys are the SAME `terms.*` / `privacy.*` keys the live pages read, so
 * these pages render the company's own reviewed legal translations rather than a
 * second, re-translated copy of the same documents. That is also why the runs are
 * split where they are: the split follows the existing keys' granularity, not
 * where a sentence happens to want an anchor.
 *
 * A value that is not a namespaced key — a street address, a phone number, the
 * "." joining two clauses — passes through `t()` unchanged, so literals are still
 * allowed for the things that are genuinely not translated.
 */

/** A run of body text: plain, a link, or bold. Each carries a translation key. */
export type Inline = string | InlineLink | InlineStrong;

export interface InlineLink {
  /** Translation key for the anchor's text. */
  text: string;
  href: string;
  /** Links to the outside world open in a new tab; mail/phone links do not. */
  newTab?: boolean;
}

export interface InlineStrong {
  /** Translation key for the bold run's text. */
  text: string;
  strong: true;
}

export const isLink = (run: Inline): run is InlineLink => typeof run !== "string" && "href" in run;

export type Block =
  | { kind: "para"; content: Inline[] }
  /** `letters` renders (a), (b), … in a 30px gutter; `bullets` renders • in 16px. */
  | { kind: "list"; markers: "letters" | "bullets"; items: Inline[][] };

export interface Section {
  /** Translation key for the clause heading, WITHOUT its number — see LegalDoc. */
  title: string;
  blocks: Block[];
}

export interface LegalDocContent {
  /** Translation key for the small-caps line above the preamble. */
  lead?: string;
  /** Paragraphs before the first numbered clause. */
  intro: Inline[][];
  /** Translation key for the one boxed aside between the preamble and the clauses. */
  callout: string;
  /** Numbered clauses. Numbers come from array order, never stored. */
  sections: Section[];
}
